import React, { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  ControllerColorPicker,
  ControllerNumberInput,
  ControllerTextInput
} from "components/basic/controller-inputs/ControllerInputs";
import {
  MdmAntennaDto,
  useCreateOrUpdateAntennaMutation,
  useDeleteAntennaMutation,
  useGetCoordinatesLazyQuery,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DweenColors } from "utils/enums";
import { z } from "zod";
import useProjectContext from "../../hooks/useProjectContext";
import { apolloErrorToaster, successToaster } from "../../utils/utils";

type MaintenanceFormProps = {
  antenna?: MdmAntennaDto;
  closeDialog?: () => void;
  newForm?: boolean;
};

const schema = z.object({
  title: z.string({ message: T("word.required-fields") }).nonempty({ message: T("word.non-empty") }),
  description: z.nullable(z.optional(z.string())),
  radius: z.number({ message: T("word.required-fields") }),
  color: z.string({ message: T("word.required-fields") }).nonempty({ message: T("word.non-empty") }),
  latitude: z.number({ message: T("word.required-fields") }),
  longitude: z.number({ message: T("word.required-fields") }),
  city: z.nullable(z.optional(z.string())),
  street: z.nullable(z.optional(z.string())),
  houseNumber: z.nullable(z.optional(z.string())),
});

const AntennaForm: React.FC<MaintenanceFormProps> = ({ antenna, closeDialog, newForm = false }) => {
  const navigate = useNavigate();
  const { projectId } = useProjectContext();
  
  const [getCoordinates, { data, loading: loadingCoord, error }] = useGetCoordinatesLazyQuery();

  const [createMaintenanceMutation, { loading }] = useCreateOrUpdateAntennaMutation({
    onCompleted: () => {
      successToaster("");
      closeDialog?.();
    },
    onError: apolloErrorToaster,
    refetchQueries: ["GetAntennasByProjectPaged", "GetAntennasByProject"],
  });

  const [deleteMaintenanceMutation, { loading: deleteLoading }] = useDeleteAntennaMutation({
    onCompleted: () => {
      successToaster("");
      navigate(`/mdm/${projectId}/antennas`);
    },
    onError: apolloErrorToaster,
    update: (cache) => {
      const normalizedId = cache.identify({ id: antenna?.id, __typename: "MdmAntennaDTO" });
      cache.evict({ id: normalizedId });
      cache.gc();
    },
  });

  const {
    control,
    handleSubmit,
    resetField,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<MdmAntennaDto>({
    defaultValues: antenna,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (data && data.coordinates) {
      setValue("latitude", data.coordinates[0]?.latitude);
      setValue("longitude", data.coordinates[0]?.longitude);
    }
  }, [data]);

  const onSubmit = (formData: MdmAntennaDto) => {
    createMaintenanceMutation({
      variables: {
        antenna: {
          ...formData,
          projectId,
          id: antenna?.id,
        },
      },
    });
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteMaintenanceMutation({
          variables: {
            id: antenna?.id!,
          },
        });
      },
    });
  };

  return (
    <form className="maintenance-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="title"
          control={control}
          render={({ field }) => <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={6} />}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => <ControllerTextInput field={field} errors={errors} label={T("word.description")} col={6} />}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="city"
          control={control}
          render={({ field }) => <ControllerTextInput field={field} errors={errors} label={T("word.city")} col={4} />}
        />
        <Controller
          name="street"
          control={control}
          render={({ field }) => <ControllerTextInput field={field} errors={errors} label={T("word.street")} col={4} />}
        />
        <Controller
          name="houseNumber"
          control={control}
          render={({ field }) => <ControllerTextInput field={field} errors={errors} label={T("word.house-number")} col={4} />}
        />
      </div>
      <div className="flex flex-row flex-wrap">
        <Button
          type="button"
          loading={loadingCoord}
          label={T("action.get-coordinates")}
          className="w-full md:w-2 md:col-2 align-self-center"
          size="small"
          onClick={() => {
            getCoordinates({
              variables: {
                address: {
                  city: getValues("city"),
                  street: getValues("street"),
                  houseNumber: getValues("houseNumber"),
                },
              },
            });
          }}
        />
        <Controller
          name="latitude"
          control={control}
          render={({ field }) => (
            <ControllerNumberInput
              field={field}
              errors={errors}
              label={T("word.latitude")}
              useGrouping={false}
              maxFractionDigits={10}
              col={3}
            />
          )}
        />
        <Controller
          name="longitude"
          control={control}
          render={({ field }) => (
            <ControllerNumberInput
              field={field}
              errors={errors}
              label={T("word.longitude")}
              useGrouping={false}
              maxFractionDigits={10}
              col={3}
            />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="radius"
          defaultValue={500}
          control={control}
          render={({ field }) => (
            <ControllerNumberInput
              field={field}
              errors={errors}
              label={T("word.radius")}
              suffix="m"
              useGrouping={false}
              min={0}
              col={3}
            />
          )}
        />
        <Controller
          name="color"
          defaultValue={DweenColors.Blue400}
          control={control}
          render={({ field }) => (
            <ControllerColorPicker
              field={field}
              errors={errors}
              label={T("word.color")}
              col={2}
            />
          )}
        />
      </div>
      {newForm && (
        <div className="flex flex-wrap gap-2 justify-content-end">
          <Button
            type="submit"
            label={T("action.create")}
            loading={loading}
            className="p-button-rounded p-button-success w-10rem"
          />
          <Button
            type="button"
            onClick={closeDialog}
            label={T("word.cancel")}
            rounded
            severity="secondary"
            outlined
            className="w-10rem"
          />
        </div>
      )}
      {!newForm && (
        <div className="flex flex-wrap gap-2 justify-content-end">
          <Button
            type="submit"
            label={T("action.update")}
            loading={loading}
            className="p-button-rounded p-button-primary w-10rem"
          />
          <Button
            type="button"
            label={T("action.delete")}
            className="p-button-rounded p-button-danger w-10rem"
            onClick={handleDelete}
            loading={deleteLoading}
          />
          <Button
            type="button"
            onClick={() => navigate(-1)}
            label={T("action.back")}
            rounded
            severity="secondary"
            outlined
            className="w-10rem"
          />
        </div>
      )}
    </form>
  );
};

export default AntennaForm;
